import { createContext, useContext, useMemo, useEffect, useCallback, useState, ReactNode } from 'react'
import { useHeaderPrefs, type HeaderMode } from './HeaderPrefsContext'

interface LayoutContextType {
  /** Current header mode for components that need to read it */
  headerMode: HeaderMode
  /** Header dimensions for programmatic use (CSS vars are the primary contract) */
  headerTopHeight: number
  headerBottomHeight: number
  headerLeftWidth: number
  headerRightWidth: number
  /** Override a specific dimension (e.g. TopShell on scroll collapse) */
  setLayoutDimension: (key: 'top' | 'bottom' | 'left' | 'right', value: number) => void
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined)

/**
 * Dimension defaults per header mode.
 * These are the "resting" values; shells can override via setLayoutDimension()
 * but these serve as the base contract.
 */
function getDimensions(mode: HeaderMode) {
  switch (mode) {
    case 'top-expanded':
      return { top: 80, bottom: 0, left: 0, right: 0 }
    case 'top-collapsed':
      return { top: 52, bottom: 0, left: 0, right: 0 }
    case 'bottom':
      return { top: 0, bottom: 56, left: 0, right: 0 }
    case 'floating':
      return { top: 0, bottom: 0, left: 0, right: 0 }
    case 'minimized':
      return { top: 0, bottom: 0, left: 0, right: 0 }
    default:
      return { top: 80, bottom: 0, left: 0, right: 0 }
  }
}

export function LayoutProvider({ children }: { children: ReactNode }) {
  const { mode } = useHeaderPrefs()

  const baseDims = useMemo(() => getDimensions(mode), [mode])

  // Overrides let shells push dynamic values (e.g. TopShell scroll collapse 80→52)
  const [overrides, setOverrides] = useState<Record<string, number>>({})

  const setLayoutDimension = useCallback((key: 'top' | 'bottom' | 'left' | 'right', value: number) => {
    setOverrides((prev) => {
      if (prev[key] === value) return prev
      return { ...prev, [key]: value }
    })
  }, [])

  // Reset overrides when mode changes (new mode gets fresh base dims)
  useEffect(() => {
    setOverrides({})
  }, [mode])

  const dims = useMemo(() => ({
    top: overrides.top ?? baseDims.top,
    bottom: overrides.bottom ?? baseDims.bottom,
    left: overrides.left ?? baseDims.left,
    right: overrides.right ?? baseDims.right,
  }), [baseDims, overrides])

  // Publish CSS custom properties on :root and dispatch layout events
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--header-top-height', `${dims.top}px`)
    root.style.setProperty('--header-bottom-height', `${dims.bottom}px`)
    root.style.setProperty('--header-left-width', `${dims.left}px`)
    root.style.setProperty('--header-right-width', `${dims.right}px`)
    root.style.setProperty('--header-mode', mode)

    // Notify listeners (Agentic chat, snap recalcs, tests)
    try {
      window.dispatchEvent(new CustomEvent('headerLayoutChange', {
        detail: { top: dims.top, bottom: dims.bottom, left: dims.left, right: dims.right, mode },
      }))
      window.dispatchEvent(new CustomEvent('headerTopChange', { detail: { height: dims.top } }))
      window.dispatchEvent(new CustomEvent('headerBottomChange', { detail: { height: dims.bottom } }))
      window.dispatchEvent(new CustomEvent('headerLeftChange', { detail: { width: dims.left } }))
      window.dispatchEvent(new CustomEvent('headerRightChange', { detail: { width: dims.right } }))
    } catch {
      // Ignore in SSR / non-browser environments
    }
  }, [dims, mode])

  const value = useMemo<LayoutContextType>(() => ({
    headerMode: mode,
    headerTopHeight: dims.top,
    headerBottomHeight: dims.bottom,
    headerLeftWidth: dims.left,
    headerRightWidth: dims.right,
    setLayoutDimension,
  }), [mode, dims, setLayoutDimension])

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  )
}

export function useLayout(): LayoutContextType {
  const ctx = useContext(LayoutContext)
  if (!ctx) {
    throw new Error('useLayout must be used within LayoutProvider')
  }
  return ctx
}
